import { Component, model, signal, computed, ChangeDetectionStrategy, inject } from '@angular/core';

import { Router } from '@angular/router';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { RippleModule } from 'primeng/ripple';
import { ProgressBarModule } from 'primeng/progressbar';

interface Step {
  number: number;
  icon: string;
  title: string;
  description: string;
  color: string;
  gradient: string;
}

@Component({
  selector: 'app-how-it-works-modal',
  imports: [
    DialogModule,
    ButtonModule,
    RippleModule,
    ProgressBarModule
],
  templateUrl: './how-it-works-modal.component.html',
  styleUrls: ['./how-it-works-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class HowItWorksModalComponent {
  private router = inject(Router);

  visible = model<boolean>(false);

  protected readonly currentStep = signal(0);

  protected readonly progressPercent = computed(
    () => ((this.currentStep() + 1) / this.steps.length) * 100
  );

  protected readonly steps: readonly Step[] = [
    {
      number: 1,
      icon: 'pi-user-plus',
      title: 'Crie sua conta',
      description: 'Cadastre-se gratuitamente em menos de 2 minutos. Sem taxas de adesão ou mensalidades.',
      color: '#F2994A',
      gradient: 'linear-gradient(135deg, #F2994A 0%, #F27121 100%)'
    },
    {
      number: 2,
      icon: 'pi-calendar-plus',
      title: 'Cadastre seu evento',
      description: 'Adicione informações do evento, tipos de ingressos, valores e configure sua página personalizada.',
      color: '#8B5CF6',
      gradient: 'linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)'
    },
    {
      number: 3,
      icon: 'pi-share-alt',
      title: 'Divulgue e venda',
      description: 'Compartilhe o link do seu evento nas redes sociais e comece a vender ingressos online 24/7.',
      color: '#3B82F6',
      gradient: 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)'
    },
    {
      number: 4,
      icon: 'pi-chart-line',
      title: 'Acompanhe em tempo real',
      description: 'Monitore vendas, receita e público através do painel administrativo com relatórios detalhados.',
      color: '#10B981',
      gradient: 'linear-gradient(135deg, #10B981 0%, #059669 100%)'
    },
    {
      number: 5,
      icon: 'pi-qrcode',
      title: 'Realize o check-in',
      description: 'No dia do evento, valide os ingressos rapidamente com nosso app de leitura de QR Code.',
      color: '#EC4899',
      gradient: 'linear-gradient(135deg, #EC4899 0%, #DB2777 100%)'
    },
    {
      number: 6,
      icon: 'pi-wallet',
      title: 'Receba seus pagamentos',
      description: 'Os valores são transferidos automaticamente para sua conta bancária de acordo com o prazo escolhido.',
      color: '#EF4444',
      gradient: 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)'
    }
  ] as const;

  protected closeModal(): void {
    this.visible.set(false);
    this.currentStep.set(0);
  }

  protected startNow(): void {
    this.closeModal();
    this.router.navigate(['/auth/registro/criador-de-evento']);
  }

  protected nextStep(): void {
    if (this.currentStep() < this.steps.length - 1) {
      this.currentStep.update(step => step + 1);
    }
  }

  protected previousStep(): void {
    if (this.currentStep() > 0) {
      this.currentStep.update(step => step - 1);
    }
  }

  protected goToStep(index: number): void {
    this.currentStep.set(index);
  }

  protected get isFirstStep(): boolean {
    return this.currentStep() === 0;
  }

  protected get isLastStep(): boolean {
    return this.currentStep() === this.steps.length - 1;
  }

  protected getStepDuration(stepNumber: number): string {
    const durations: Record<number, string> = {
      1: '2 min',
      2: '5 min',
      3: '1 min',
      4: 'Contínuo',
      5: 'No evento',
      6: 'Automático'
    };
    return durations[stepNumber] || '5 min';
  }

  protected getStepDetails(stepNumber: number): string[] {
    const details: Record<number, string[]> = {
      1: [
        'Cadastro 100% gratuito',
        'Sem taxas de adesão',
        'Verificação rápida por e-mail',
        'Acesso imediato ao painel'
      ],
      2: [
        'Crie quantos eventos quiser',
        'Configure tipos de ingressos',
        'Defina valores e lotes',
        'Personalize sua página'
      ],
      3: [
        'Link único para compartilhar',
        'Integração com redes sociais',
        'Vendas 24 horas por dia',
        'Sem limite de ingressos'
      ],
      4: [
        'Dashboard em tempo real',
        'Gráficos e estatísticas',
        'Relatórios exportáveis',
        'Acompanhe de qualquer lugar'
      ],
      5: [
        'App de validação gratuito',
        'Leitura rápida de QR Code',
        'Funciona offline',
        'Controle total de entrada'
      ],
      6: [
        'Transferência automática',
        'Escolha o prazo de recebimento',
        'Sem taxas ocultas',
        'Extrato detalhado'
      ]
    };
    return details[stepNumber] || [];
  }

  protected getStepBenefits(stepNumber: number): string[] {
    const benefits: Record<number, string[]> = {
      1: [
        'Comece a vender em minutos',
        'Sem necessidade de conhecimento técnico',
        'Suporte completo desde o início'
      ],
      2: [
        'Interface intuitiva e fácil',
        'Templates prontos para usar',
        'Customização total do seu evento'
      ],
      3: [
        'Alcance mais público',
        'Vendas automáticas',
        'Compartilhamento facilitado'
      ],
      4: [
        'Decisões baseadas em dados',
        'Visão completa do seu evento',
        'Otimize suas vendas'
      ],
      5: [
        'Entrada rápida e organizada',
        'Reduza filas e espera',
        'Experiência profissional'
      ],
      6: [
        'Receba sem complicações',
        'Transparência total',
        'Controle financeiro completo'
      ]
    };
    return benefits[stepNumber] || [];
  }

  protected readonly features = [
    {
      icon: 'pi-shield-check',
      title: 'Segurança Total',
      description: 'Pagamentos protegidos'
    },
    {
      icon: 'pi-headphones',
      title: 'Suporte 24/7',
      description: 'Sempre disponível'
    },
    {
      icon: 'pi-chart-line',
      title: 'Relatórios',
      description: 'Análises detalhadas'
    },
    {
      icon: 'pi-mobile',
      title: 'Mobile First',
      description: 'Funciona em qualquer dispositivo'
    }
  ];
}
